import { FC, useCallback, useEffect, useMemo } from 'react';
import {
  Stack,
  Group,
  Button,
  Title,
  Paper,
  Loader,
  Center,
} from '@mantine/core';
import { IconPlus, IconClipboardList } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, ServiceRequest } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { useMedplum, useResource } from '@medplum/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import { ResourceTable } from '@/components/resource-search/ResourceTable';
import {
  useOrderSearch,
  ORDER_SEARCH_DEFAULTS,
  OrderSearchParams,
} from '../hooks/useOrderSearch';
import {
  getOrderSearchFields,
  getOrderTableColumns,
} from './orderSearchConfig';

interface OrdersListProps {
  patientId?: string;
  hideCreateButton?: boolean;
}

export const OrdersList: FC<OrdersListProps> = ({
  patientId,
  hideCreateButton = false,
}) => {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const location = useLocation();
  const { t } = useTranslation('lims');
  const patient = useResource<Patient>(
    patientId ? { reference: `Patient/${patientId}` } : undefined
  );

  // Use the order search hook
  const {
    resources: orders,
    loading,
    page,
    totalPages,
    totalCount,
    setPage,
    patients,
    consents,
    searchParams,
    updateSearchParams,
    clearSearch,
    fetchResources: fetchOrders,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
    resourceIdQuery: orderIdQuery,
    setResourceIdQuery: setOrderIdQuery,
    resourceIdOptions: orderIdOptions,
    resourceIdLoading: orderIdLoading,
  } = useOrderSearch(medplum, patientId);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleSearch = useCallback(
    (updates: Partial<OrderSearchParams>) => {
      const merged = { ...searchParams, ...updates };
      setPage(1);
      updateSearchParams(updates);
      fetchOrders(merged);
    },
    [fetchOrders, searchParams, setPage, updateSearchParams]
  );

  const handleClearSearch = useCallback(() => {
    clearSearch(ORDER_SEARCH_DEFAULTS);
  }, [clearSearch]);

  // ---------------------------------------------------------------------------
  const handleCreateOrder = useCallback(() => {
    navigate('/lims/new');
  }, [navigate]);

  const fields = useMemo(
    () =>
      getOrderSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
        hidePatientName: !!patientId,
        patientNameQuery,
        setPatientNameQuery,
        setPatientQuery,
        patientNameOptions,
        patientLoading,
        patientNameLoading,
        orderIdQuery,
        setOrderIdQuery,
        orderIdOptions,
        orderIdLoading,
      }),
    [
      handleSearch,
      orderIdLoading,
      orderIdOptions,
      orderIdQuery,
      patientId,
      patientLoading,
      patientNameLoading,
      patientNameOptions,
      patientNameQuery,
      searchParams,
      setOrderIdQuery,
      setPatientNameQuery,
      setPatientQuery,
      t,
    ]
  );

  const columns = useMemo(
    () =>
      getOrderTableColumns({
        t,
        patients,
        consents,
        hidePatientColumn: !!patientId,
      }),
    [patients, consents, patientId, t]
  );

  const handleOrderClick = useCallback(
    (order: ServiceRequest) => {
      if (order.id) {
        navigate(`/lims/order/${order.id}`);
      }
    },
    [navigate]
  );

  return (
    <Stack gap="md" className="p-6">
      <Group justify="space-between">
        <Group gap="sm">
          <IconClipboardList size={24} />
          <Title order={2}>
            {patientId
              ? t('orderList.patientOrders', 'Orders for {{patientName}}', {
                  patientName: formatHumanName(patient?.name?.[0]),
                })
              : t('orderList.allOrders', 'All Orders')}
          </Title>
        </Group>
        {!hideCreateButton && (
          <Button
            variant="light"
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateOrder}
          >
            {t('orderList.createOrder', 'Create Order')}
          </Button>
        )}
      </Group>

      <ResourceSearchForm<OrderSearchParams>
        fields={fields}
        searchParams={searchParams}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        clearLabel={t('orderList.clear', { ns: 'lims' })}
        layout={{ cols: { base: 1, sm: 2, lg: patientId ? 3 : 4 } }}
      />

      <Paper shadow="sm" withBorder>
        {loading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : (
          <ResourceTable<ServiceRequest>
            data={orders}
            columns={columns}
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            onPageChange={setPage}
            onRowClick={handleOrderClick}
            onActionClick={handleOrderClick}
            emptyText={t('orderList.noOrdersFound', 'No orders found', {
              ns: 'lims',
            })}
          />
        )}
      </Paper>
    </Stack>
  );
}
